import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { number } from 'mathjs';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject, throwError } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { Availability, Filter, PaginationPage, PaginationResponse } from 'src/app/classes';
import { serverLocation } from 'src/app/globals';
import { TokenService } from '../../shared/services/token.service';
import { Address, AvailabilityComponent, AvailabilityRequirement, AvailabilityStatus, Location } from '../classes';

@Injectable({
  providedIn: 'root'
})
export class LocationService {

  private _addresses: { [key: number]: ReplaySubject<Address> } = {};
  addressSearches: { [key: string]: BehaviorSubject<Address[]> } = {};

  private _locations: { [key: number]: ReplaySubject<Location> } = {};
  locations$ = combineLatest(Object.values(this._locations).map(x => x.asObservable()));

  private _locationAvailabilities: { [key: number]: ReplaySubject<Availability[]> } = {};

  private _components = new ReplaySubject<AvailabilityComponent[]>(1);
  components$ = this._components.asObservable();
  private _componentsLoaded = new BehaviorSubject(false);

  private _requirements = new ReplaySubject<AvailabilityRequirement[]>(1);
  requirements$ = this._requirements.asObservable();
  private _requirementsLoaded = new BehaviorSubject(false);

  private _status = new ReplaySubject<AvailabilityStatus[]>(1);
  status$ = this._status.asObservable();
  private _statusLoaded = new BehaviorSubject(false);

  constructor(
    private _tokenService: TokenService,
    private _httpClient: HttpClient
  ) { }

  clearLocationHistory = () => this._httpClient.delete(`${serverLocation}/api/location/history`, {
    headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
  });

  createAddress(address: Address): Observable<Address> {
    return this._httpClient.post(`${serverLocation}/api/address`, address, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Address>;
  }

  createAvailability(availability: Availability): Observable<Availability> {
    return this._httpClient.post(`${serverLocation}/api/availability`, availability, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Availability>;
  }

  createLocation(location: Location, note: string = null): Observable<Location> {
    return this._httpClient.post(`${serverLocation}/api/location`, {
      note: note,
      content: location
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Location>;
  }

  deleteAvailability(availabilityId: number): Observable<Object> {
    return this._httpClient.delete(`${serverLocation}/api/availability/${availabilityId}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Object>;
  }

  deleteLocation(locationId: number): Observable<Object> {
    let subject = new Subject();
    this._deleteLocationById(locationId).pipe(tap(() => {
      if (this._locations[locationId]) {
        this._locations[locationId].complete();
        delete this._locations[locationId];
      }
    })).subscribe(subject);
    return subject;
  }

  getAddressById(identifier: number, soft: boolean = true): Observable<Address> {
    if (typeof identifier !== 'number') return throwError('no valid address identifier passed');
    if (!soft || !this._addresses[identifier]) {
      if (!this._addresses[identifier]) this._addresses[identifier] = new ReplaySubject(1);
      this._getAddressById(identifier).subscribe({
        next: (address: Address) => this._addresses[identifier].next(address),
        error: (error: HttpErrorResponse) => this._addresses[identifier].error(error)
      });
    }
    return this._addresses[identifier].asObservable();
  }

  getLocationAvailabilities(identifier: number, soft: boolean = true): Observable<Availability[]> {
    if (!soft || !this._locationAvailabilities[identifier]) {
      if (!this._locationAvailabilities[identifier]) this._locationAvailabilities[identifier] = new ReplaySubject<Availability[]>(1);
      (this._httpClient.get(`${serverLocation}/api/location/${identifier}/availabilities`, {
        headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
      }) as Observable<Availability[]>).subscribe({
        next: (result: Availability[]) => this._locationAvailabilities[identifier].next(result),
        error: (error) => this._locationAvailabilities[identifier].error(error)
      });
    }
    return this._locationAvailabilities[identifier].asObservable();
  }

  getLocationById(identifier: number, soft: boolean = true): Observable<Location> {
    if (typeof identifier !== 'number') return throwError('no valid location identifier passed');
    if (!soft || !this._locations[identifier]) {
      if (!this._locations[identifier]) this._locations[identifier] = new ReplaySubject(1);
      this._getLocationById(identifier).subscribe({
        next: (address: Location) => this._locations[identifier].next(address),
        error: (error: HttpErrorResponse) => this._locations[identifier].error(error)
      });
    }
    return this._locations[identifier].asObservable();
  }

  getUserLocationHistory(page: PaginationPage) {
    return this._httpClient.post(`${serverLocation}/api/location/history`, page, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<Location[]>>;
  }

  getWatchedLocations(page: PaginationPage) {
    return this._httpClient.post(`${serverLocation}/api/location/watched`, page, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<Location[]>>;
  }

  loadAvailabilityComponents(): Observable<AvailabilityComponent[]> {
    return this._componentsLoaded.pipe(switchMap((loaded: boolean) => {
      if (!loaded) this._getAvailabilityComponents().subscribe({
        next: (result: AvailabilityComponent[]) => this._components.next(result),
        error: (error) => this._components.error(error)
      })
      return this._components.asObservable();
    }));
  }

  loadAvailabilityRequirements(): Observable<AvailabilityRequirement[]> {
    return this._requirementsLoaded.pipe(switchMap((loaded: boolean) => {
      if (!loaded) this._getAvailabilityRequirements().subscribe({
        next: (result: AvailabilityRequirement[]) => this._requirements.next(result),
        error: (error) => this._requirements.error(error)
      })
      return this._requirements.asObservable();
    }));
  }

  loadAvailabilityStatus(): Observable<AvailabilityStatus[]> {
    return this._statusLoaded.pipe(switchMap((loaded: boolean) => {
      if (!loaded) this._getAvailabilityStatus().subscribe({
        next: (result: AvailabilityStatus[]) => this._status.next(result),
        error: (error) => this._status.error(error)
      })
      return this._status.asObservable();
    }));
  }

  locationIsWatched(locationId: number): Observable<boolean> {
    return this._httpClient.get(`${serverLocation}/api/location/${locationId}/watched`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<boolean>;
  }

  saveHistory(locationId: number): Observable<Object> {
    let subject = new Subject();
    this._httpClient.get(`${serverLocation}/api/location/history/${locationId}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }).subscribe(subject);
    return subject.asObservable();
  }

  searchAddresses(filter: Filter, page: PaginationPage = null): Observable<PaginationResponse<Address[]>> {
    return this._httpClient.post(`${serverLocation}/api/address/search`, {
      filter: filter,
      page: page
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<Address[]>>
  }

  searchLocationsByString(search: string, paginationPage: PaginationPage = { pageNumber: 1, pageSize: 20 }) {
    return this._httpClient.post(`${serverLocation}/api/location/search`, {
      'searchString': search,
      'page': paginationPage
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<Location[]>>;
  }

  searchLocationsByUserFilter(identifier: number, paginationPage: PaginationPage = { pageNumber: 1, pageSize: 20 }) {
    return this._httpClient.post(`${serverLocation}/api/location/user-filter-search/${identifier}`, paginationPage, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<Location[]>>;
  }

  setLocationWatchedStatus(locationId: number, watch: boolean): Observable<boolean> {
    return this._httpClient.get(`${serverLocation}/api/location/${locationId}/${watch? 'watch': 'unwatch'}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<boolean>;
  }

  updateAddress(address: Address): Observable<Address> {
    return this._httpClient.put(`${serverLocation}/api/address`, address, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Address>;
  }


  updateAvailability(availability: Availability): Observable<Availability> {
    return this._httpClient.put(`${serverLocation}/api/availability`, availability, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Availability>;
  }

  updateLocation(location: Location, note: string = null): Observable<Location> {
    return this._httpClient.put(`${serverLocation}/api/location`, {
      note: note,
      content: location
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Location>;
  }

  private _deleteLocationById(locationId: number): Observable<Object> {
    return this._httpClient.delete(`${serverLocation}/api/location/${locationId}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Object>;
  }

  private _getAddressById(identifier: number): Observable<Address> {
    return this._httpClient.get(`${serverLocation}/api/address/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Address>;
  }

  private _getLocationById(identifier: number): Observable<Location> {
    return this._httpClient.get(`${serverLocation}/api/location/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Location>;
  }

  private _getAvailabilityComponents(): Observable<AvailabilityComponent[]> {
    this._componentsLoaded.next(true);
    return (this._httpClient.get(`${serverLocation}/api/availability-component`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<AvailabilityComponent[]>);
  }

  private _getAvailabilityRequirements(): Observable<AvailabilityRequirement[]> {
    this._requirementsLoaded.next(true);
    return (this._httpClient.get(`${serverLocation}/api/availability-requirements`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<AvailabilityRequirement[]>);
  }

  private _getAvailabilityStatus(): Observable<AvailabilityStatus[]> {
    this._statusLoaded.next(true);
    return (this._httpClient.get(`${serverLocation}/api/availability-status`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<AvailabilityStatus[]>);
  }
}
